import { default as Cash, default as MoneyBag } from "@/assets/cash.png";
import Emoji from "@/assets/happyEmoji.png";
import { CalculatorState } from "@/types/calculatorTypes";
import React, { ChangeEvent } from "react";
import ButtonSlider from "../Slider/ButtonSlider";
import { ContributionCalculator } from "./CalculationEngine";

interface SimplifiedCalculatorProps {
  calculatorState: CalculatorState;
  onSalaryChange: (salary: number) => void;
  onContributionRateChange: (rate: number) => void;
  handleEditDetails: () => void;
}

const SimplifiedCalculator: React.FC<SimplifiedCalculatorProps> = ({
  calculatorState,
  onSalaryChange,
  onContributionRateChange,
  handleEditDetails,
}) => {
  const results = ContributionCalculator.calculate(calculatorState);

  const handleSalaryInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value.replace(/[^\d]/g, "");
    onSalaryChange(parseInt(value) || 0);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Round value for ButtonSlider to prevent decimal display issues
  const roundedEmployeeContributionRate = Math.round(
    calculatorState.employeeContributionRate
  );

  return (
    <>
      <div className="flex flex-col space-y-[16px] ">
        <div className="flex justify-between items-center">
          <p>Annual Salary</p>
          <div className="w-[65%]">
            <input
              type="text"
              value={formatCurrency(calculatorState.annualSalary)}
              onChange={handleSalaryInputChange}
              style={{ boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.07) inset" }}
              className="w-full bg-[#F8F8F8] rounded-[100px] px-[12px] py-[16px] text-center text-[18px] font-normal text-black border-[1px] border-[#E2E2E2] outline-none focus:ring-2 focus:ring-black"
            />
          </div>
        </div>

        <ButtonSlider
          label="Contribution"
          value={roundedEmployeeContributionRate}
          onChange={onContributionRateChange}
          min={1}
          max={100}
        />

        <div className="pt-[28px] pb-[16px] leading-[127%]">
          <div className="p-[24px] bg-brand-default text-white text-center rounded-t-[16px]">
            <p className="text-[16px] font-normal">
              After 12 months you will likely have around
            </p>
            <div className="mt-[4px] text-[24px] font-semibold">
              {formatCurrency(
                results.totalEmployeeContributions +
                  results.totalEmployerContributions
              )}
              *
            </div>
          </div>
          <div className="flex flex-col space-y-[12px] leading-[127%] p-[24px] bg-[#008E8D] rounded-b-[16px]">
            <p className="text-white/60">So, during each payroll cycle:</p>

            <div className="text-white flex items-center space-x-4 ">
              <div className="bg-white rounded-full p-2 flex-shrink-0 shadow-lg">
                <img src={Cash} className="w-[18px] h-[18px]" alt="Cash" />
              </div>
              <div className="flex-1 pt-1">
                <p className="text-[16px] font-normal">
                  Approx.{" "}
                  <span className="font-semibold">
                    {formatCurrency(results.employeeContributionPerPayroll)}
                  </span>{" "}
                  will be deducted from your paycheck.
                </p>
              </div>
            </div>

            <div className="text-white flex items-center space-x-4 ">
              <div className="bg-white rounded-full p-2 flex-shrink-0 shadow-lg">
                <img src={Emoji} className="w-[18px] h-[18px]" alt="Emoji" />
              </div>
              <div className="flex-1 pt-1">
                <p className="text-[16px] font-normal">
                  Approx.{" "}
                  <span className="font-semibold">
                    {formatCurrency(results.employerMatchPerPayroll)}
                  </span>{" "}
                  will be matched by your employer
                </p>
              </div>
            </div>

            <div className="text-white flex items-center space-x-4 ">
              <div className="bg-white rounded-full p-2 flex-shrink-0 shadow-lg">
                <img
                  src={MoneyBag}
                  className="w-[18px] h-[18px]"
                  alt="MoneyBag"
                />
              </div>
              <div className="flex-1 pt-1">
                <p className="text-[16px] font-normal">
                  You'll save{" "}
                  <span className="font-semibold">
                    {formatCurrency(results.totalContributionPerPayroll)}
                  </span>{" "}
                  total each paycheck
                </p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-[16px] font-normal leading-[127%] text-black/45">
          *Based on your employer's plan: {calculatorState.employerMatchRate}%
          match up to {calculatorState.maxEmployerMatchSalaryRate}% of salary,{" "}
          {formatCurrency(calculatorState.maxAnnualContribution)} annual
          maximum, and the {calculatorState.annualGrowthRate}% projected growth.{" "}
          <span
            onClick={handleEditDetails}
            className="cursor-pointer text-[#00ADAC]"
          >
            Edit details
          </span>
        </p>
      </div>
    </>
  );
};

export default SimplifiedCalculator;
